import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Workspace from "@/models/workspace/Workspace";
import { WorkspaceDocumentType } from "@/models/workspace/Workspace";
import { Item } from "@/pages/workspace/utils/workspaceItemUtils";
import {
  PathItem,
  findFolderById,
  getFolderPathFromUrl,
  buildPathFromNames,
  findPathToFolder,
} from "@/pages/workspace/utils/folderUtils";
import {
  getRootLevelItems,
  getFolderItems,
  ITEM_TYPE_LINK,
} from "@/pages/workspace/utils/workspaceItemUtils";
import AppRoutes from "@/constants/routes/AppRoutes";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import {
  shouldOpenInNewTab,
  workspaceDocumentTarget,
} from "@/lib/navigation/appNavigation";
import type { SelectedWorkspaceVersion } from "@/models/workspace/Workspace";

interface UseFilesTabNavigationProps {
  workspace: Workspace | null;
  workspaceId: string;
  openFolderId?: string;
  setSelectedItems: (items: string[]) => void;
}

interface UseFilesTabNavigationReturn {
  // State
  currentPath: PathItem[];
  currentItems: Item[];
  isNavigating: boolean;

  // Functions
  handleRowClick: (item: Item, event?: React.MouseEvent) => void;
  navigateToPath: (index: number) => void;
}

/**
 * Hook for handling navigation and path management in FilesTab
 *
 * Manages:
 * - Current folder path and items
 * - URL synchronization with folder navigation
 * - Session storage for document navigation
 * - Folder expansion from URL state
 * - Document click handling and HTML fetching
 *
 * The hook synchronizes the current folder path with the URL, allowing
 * users to bookmark specific folders and navigate back/forward in browser history.
 */
export const useFilesTabNavigation = ({
  workspace,
  workspaceId,
  openFolderId,
  setSelectedItems,
}: UseFilesTabNavigationProps): UseFilesTabNavigationReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openNavigation } = useOpenAppNavigation();
  const { setSelectedVersion } = useWorkspaceDocumentVersionStore();

  // Navigation state
  const [currentPath, setCurrentPath] = useState<PathItem[]>([]);
  const [currentItems, setCurrentItems] = useState<Item[]>([]);
  const [isNavigating, setIsNavigating] = useState(false);

  // expandFolder utility to set currentPath/currentItems to the folder with openFolderId
  const expandFolder = (folderId: string) => {
    if (!workspace) return;
    const folderPath = findPathToFolder(workspace.folders, folderId, []);
    if (folderPath) {
      setCurrentPath(folderPath);
      const lastFolder = findFolderById(workspace, folderId);
      if (lastFolder) {
        setCurrentItems(getFolderItems(lastFolder, currentPath));
      }
    }
  };

  const handleRowClick = (item: Item, event?: React.MouseEvent) => {
    if (item.type === ITEM_TYPE_LINK) {
      if (!item.url) {
        logger.warn("Workspace link row has no url to open", {
          operation: "openWorkspaceLink",
          workspaceId,
          linkId: item.id,
        });
        showErrorToast("Linket mangler en webadresse.");
        return;
      }
      // A link always opens in a new tab, so the files view stays where it is.
      window.open(item.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (item.type === "folder") {
      setIsNavigating(true);
      // Use filepath to navigate to the correct folder location
      if (item.filePath) {
        // Split the filepath to get the folder hierarchy
        const pathSegments = item.filePath.split("/");
        const folderPath = pathSegments
          .map((segment) => encodeURIComponent(segment))
          .join("/");

        navigate(AppRoutes.workspaceFilesFolder(workspaceId, folderPath), {
          state: { resetSearch: true },
        });
      } else {
        // Fallback to current behavior for items without filepath
        const newPath = [...currentPath, { id: item.id, name: item.name }];
        const folderPath = newPath
          .map((p) => encodeURIComponent(p.name))
          .join("/");
        navigate(AppRoutes.workspaceFilesFolder(workspaceId, folderPath), {
          state: { resetSearch: true },
        });
      }
      setIsNavigating(false);
    } else {
      // Document clicked - try to fetch HTML first
      if (!item.documentType || !item.blobName) {
        logger.warn("Workspace document has no document type or blob name", {
          operation: "openWorkspaceItem",
          workspaceId,
          documentId: item.id,
          hasDocumentType: Boolean(item.documentType),
          hasBlobName: Boolean(item.blobName),
        });
        showErrorToast("Dokument type eller blobnavn mangler.");
        return;
      }

      sessionStorage.setItem(
        `fromFolderPath:${workspaceId}`,
        JSON.stringify(currentPath)
      );

      const versionSelection: SelectedWorkspaceVersion = {
        blobName: item.blobName,
        name: item.name,
        updatedAt: new Date(item.updatedAt || ""),
        revisionVersion: item.activeRevision || 0,
        size: item.size || 0,
        documentType: item.documentType || WorkspaceDocumentType.PDF,
      };

      const applyVersionSelection = () => {
        setSelectedVersion(versionSelection);
      };

      if (event && shouldOpenInNewTab(event)) {
        applyVersionSelection();
      }

      openNavigation(workspaceDocumentTarget(workspaceId, item.blobName), event, {
        onBeforeSameTabNavigate: applyVersionSelection,
      });
    }
  };

  const navigateToPath = (index: number) => {
    if (index === -1) {
      // Navigate to root
      navigate(AppRoutes.workspaceFiles(workspaceId));
    } else {
      // Navigate to specific path using folder names
      const newPath = currentPath.slice(0, index + 1);
      const folderPath = newPath
        .map((p) => encodeURIComponent(p.name))
        .join("/");
      navigate(AppRoutes.workspaceFilesFolder(workspaceId, folderPath));
    }
    setSelectedItems([]);
  };

  // Effect: Workspace sync - update currentItems when workspace changes
  useEffect(() => {
    if (workspace) {
      if (currentPath.length === 0) {
        setCurrentItems(getRootLevelItems(workspace));
      } else {
        const lastPath = currentPath[currentPath.length - 1];
        const folder = findFolderById(workspace, lastPath.id);
        if (folder) {
          setCurrentItems(getFolderItems(folder, currentPath));
        } else {
          setCurrentItems(getRootLevelItems(workspace));
          setCurrentPath([]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace]);

  // Effect: Expand folder if openFolderId is provided on initial render
  useEffect(() => {
    if (openFolderId && workspace) {
      expandFolder(openFolderId);
    }
    // Only run on mount or when openFolderId/workspace changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFolderId, workspace]);

  // Effect: Handle URL-based folder navigation - sync state with URL
  useEffect(() => {
    if (workspace) {
      const urlFolderNames = getFolderPathFromUrl(location);

      if (urlFolderNames.length > 0) {
        const newPath = buildPathFromNames(workspace, urlFolderNames);
        setCurrentPath(newPath);

        // Set current items based on the path
        if (newPath.length === 0) {
          setCurrentItems(getRootLevelItems(workspace));
        } else {
          const lastFolder = findFolderById(
            workspace,
            newPath[newPath.length - 1].id
          );
          if (lastFolder) {
            setCurrentItems(getFolderItems(lastFolder, newPath));
          }
        }
      } else {
        // No folder path in URL, go to root
        setCurrentPath([]);
        setCurrentItems(getRootLevelItems(workspace));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, location.pathname]);

  // Effect: Restore path from session storage when returning from document view
  useEffect(() => {
    const storedPath = sessionStorage.getItem(`fromFolderPath:${workspaceId}`);
    if (storedPath && workspace) {
      const folderPath = JSON.parse(storedPath);
      setCurrentPath(folderPath);

      const lastFolderId = folderPath[folderPath.length - 1]?.id;
      const folder = findFolderById(workspace, lastFolderId);
      if (folder) {
        setCurrentItems(getFolderItems(folder, folderPath));
      }

      sessionStorage.removeItem(`fromFolderPath:${workspaceId}`);
    }
  }, [workspace, workspaceId]);

  return {
    // State
    currentPath,
    currentItems,
    isNavigating,

    // Functions
    handleRowClick,
    navigateToPath,
  };
};
